import { Star, Calendar, CheckCircle2, XCircle, Pencil, Circle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

interface VenueReviewCardProps {
  review: any;
}

const profileTypeLabels: Record<string, string> = {
  banda: "Banda",
  artista: "Artista",
  promotora: "Promotora",
  manager: "Manager",
  tecnico: "Técnico/a",
  otro: "Otro",
};

const serviceFields = [
  { key: "sound_tech", label: "Técnico de sonido" },
  { key: "technical_rider", label: "Rider técnico" },
  { key: "merchandising_area", label: "Zona de merchandising" },
  { key: "ticketing_required", label: "Ticketing", detailKey: "ticketing_provider" },
  { key: "backline_available", label: "Backline" },
  { key: "audio_recording", label: "Grabación de audio" },
  { key: "poster_design", label: "Cartelería" },
];

const noteFields = [
  { key: "technical_rider_notes", label: "Rider técnico" },
  { key: "merch_area_details", label: "Zona de merchandising" },
  { key: "backline_details", label: "Backline" },
  { key: "sound_tech_notes", label: "Técnico de sonido" },
];

function ServiceBool({ value, label, detail }: { value: boolean | null; label: string; detail?: string | null }) {
  const isNull = value === null || value === undefined;
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {isNull ? (
        <Circle className="h-3 w-3 text-muted-foreground/30 shrink-0" />
      ) : value ? (
        <CheckCircle2 className="h-3 w-3 text-foreground shrink-0" />
      ) : (
        <XCircle className="h-3 w-3 text-muted-foreground/40 shrink-0" />
      )}
      <span className={isNull ? "text-muted-foreground/40" : value ? "text-foreground" : "text-muted-foreground/50"}>
        {label}
      </span>
      {value && detail && (
        <span className="text-muted-foreground/60 italic">· {detail}</span>
      )}
    </div>
  );
}

function MiniRatingBar({ label, value, max = 5 }: { label: string; value: number; max?: number }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-[11px] text-muted-foreground shrink-0">{label}</span>
      <Progress value={pct} className="h-1.5 flex-1 [&>div]:bg-warning" />
      <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-bold shrink-0">
        {value.toFixed(1)}
      </Badge>
    </div>
  );
}

function HalfStarDisplay({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        const halfFilled = !filled && value >= n - 0.5;
        return (
          <div key={n} className="relative">
            <Star className="h-3.5 w-3.5 text-muted-foreground/25" />
            {(filled || halfFilled) && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: halfFilled ? "50%" : "100%" }}
              >
                <Star className="h-3.5 w-3.5 fill-warning text-warning" />
              </div>
            )}
          </div>
        );
      })}
      <span className="ml-1 text-sm font-bold">{Number(value).toFixed(1)}</span>
    </div>
  );
}

export function VenueReviewCard({ review }: VenueReviewCardProps) {
  const profile = review.profiles;
  const authorName = profile?.visible_name || profile?.band_name || profile?.username || "Anónimo";
  const profileType = profile?.profile_type;
  const avatarUrl = profile?.avatar_url;
  const initials = authorName.slice(0, 2).toUpperCase();

  const hasExperience = review.sound_rating || review.staff_rating || review.price_paid;
  const hasServices = serviceFields.some((f) => review[f.key] !== null && review[f.key] !== undefined);
  const notes = noteFields.filter((f) => review[f.key]);

  return (
    <div className="rounded-lg border p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={avatarUrl} alt={authorName} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold">{authorName}</p>
              {profileType && profileTypeLabels[profileType] && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {profileTypeLabels[profileType]}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {review.event_date && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {new Date(review.event_date).toLocaleDateString("es-ES")}
                </p>
              )}
              {review.is_edited && review.edited_at && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Pencil className="h-3 w-3" />
                  Editada {new Date(review.edited_at).toLocaleDateString("es-ES")}
                </p>
              )}
            </div>
          </div>
        </div>
        <HalfStarDisplay value={Number(review.overall_rating)} />
      </div>

      {/* EXPERIENCIA */}
      {hasExperience && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Experiencia
          </p>
          <div className="flex flex-wrap items-end gap-4">
            {review.sound_rating && (
              <div className="w-32">
                <MiniRatingBar label="Sonido" value={Number(review.sound_rating)} />
              </div>
            )}
            {review.staff_rating && (
              <div className="w-32">
                <MiniRatingBar label="Staff" value={Number(review.staff_rating)} />
              </div>
            )}
            {review.price_paid && (
              <div>
                <p className="text-[11px] text-muted-foreground">Precio pagado</p>
                <p className="text-sm font-bold">{review.price_paid}€</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SERVICIOS */}
      {hasServices && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Servicios
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            {serviceFields.map((f) => (
              <ServiceBool
                key={f.key}
                value={review[f.key]}
                label={f.label}
                detail={f.detailKey ? review[f.detailKey] : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* NOTAS */}
      {notes.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Notas
          </p>
          <div className="space-y-1">
            {notes.map((n) => (
              <p key={n.key} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{n.label}:</span>{" "}
                {review[n.key]}
              </p>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
